import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_PROJECT_COLOR, type ProjectRole } from '@forecastify/shared';
import { Button, Dialog, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { ColorPicker } from '../delivery/color-picker';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C31 · Workspace → Projekt szerepek. `2fazis.markup.html` 2279.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **NOT `settings/permissions`, AND THE RAIL PUTS THEM FIVE ROWS APART FOR A REASON.** That
 * screen decides what somebody MAY DO; this one names what somebody DOES. They share a word and
 * nothing else — `project-role.service.ts` and migration `0058` make the same argument at their own
 * layer, and it is repeated here because a settings rail with two entries called *roles* is where a
 * reader would conflate them.
 *
 * ⚠️ **Reordering is two buttons, not a drag** — `vocabulary-section.tsx` argues it: the mockup's
 * `draggable` rows have no keyboard path at all, and these send the same whole-list request.
 */

/**
 * ⚠️ **The palette's first entry, not the mockup's `#7554D2` literal.** A hex in component code is
 * a ninth theme applied to one element — the lint rule says so — and `ColorPicker` offers exactly
 * this list, so a default outside it would be a swatch the picker cannot show as selected.
 */
const DEFAULT_COLOR = DEFAULT_PROJECT_COLOR;

export function ProjectRolesSection({
  loading,
  roles,
  canManage,
  serverError,
  onCreate,
  onUpdate,
  onDelete,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly roles: readonly ProjectRole[];
  readonly canManage: boolean;
  readonly serverError: string | null;
  /** 26 Oct 2026 · resolve to whether the save landed; the dialog closes on `true` only. */
  readonly onCreate: (body: { name: string; color: string }) => Promise<boolean>;
  readonly onUpdate: (
    id: string,
    body: { name: string; color: string; version: number },
  ) => Promise<boolean>;
  readonly onDelete: (id: string) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [editing, setEditing] = useState<ProjectRole | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ProjectRole | null>(null);
  const [query, setQuery] = useState('');

  /* ⚠️ Filtered here: `DataList` searches nothing on its own — the screen owns its rows. */
  const shownRoles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle === '' ? roles : roles.filter((one) => one.name.toLowerCase().includes(needle));
  }, [roles, query]);

  const rows: SettingsRow[] = shownRoles.map((role) => ({
    id: role.id,
    onOpen: !canManage
      ? undefined
      : () => {
          setEditing(role);
        },
    cells: [
      { kind: 'name' as const, text: role.name, color: role.color },
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: role.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil' as const,
                onSelect: () => {
                  setEditing(role);
                },
              },
              { type: 'separator' as const },
              {
                /* \u26a0\ufe0f `destructive`, not `danger` \u2014 `dropdown-menu.tsx`'s own note. */
                label: t('common.delete'),
                icon: 'trash' as const,
                destructive: true,
                onSelect: () => {
                  setDeleting(role);
                },
              },
            ],
          }
        : { kind: 'blank' as const },
    ],
  }));

  return (
    <div data-screen="settings-project-roles" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.projectRoles')}
        action={
          !canManage
            ? undefined
            : {
                label: t('settings.projectRoles.new'),
                icon: 'plus',
                onSelect: () => {
                  setEditing('new');
                },
              }
        }
        search={query}
        onSearch={setQuery}
        searchPlaceholder={t('settings.projectRoles.search')}
        error={serverError}
      >
        {/* \u26a0\u26a0 **NO DRAG COLUMN \u2014 asked for by name.** Unlike a task status, whose order is
            the Kanban board, a project role carries no order that means anything. */}
        <SettingsTable
          grid="minmax(0,1fr) 36px"
          minWidth={520}
          columns={[{ label: t('settings.projectRoles.column.name') }, {}]}
          groups={[{ rows }]}
          loading={loading}
          empty={{
            icon: 'users',
            title: t('settings.projectRoles.empty'),
            body: t('settings.projectRoles.emptyBody'),
          }}
        />
      </SettingsPage>

      <RoleDialog
        editing={editing}
        busy={saving}
        serverError={saveFailed ? serverError : null}
        onClose={() => {
          setSaveFailed(false);
          setEditing(null);
        }}
        onSubmit={(name, color) => {
          if (editing === null) return;
          /* 26 Oct 2026 · closed only once it lands; a refusal stays, shown in the dialog. */
          setSaving(true);
          void (
            editing === 'new'
              ? onCreate({ name, color })
              : onUpdate(editing.id, { name, color, version: editing.version })
          ).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setEditing(null);
          });
        }}
      />

      {/**
       * ⚠️⚠️ **THE CONFIRM SAYS WHAT HAPPENS TO THE MEMBERSHIPS, BECAUSE IT IS NOT WHAT A READER
       * EXPECTS.** Deleting a role does not remove anybody from a project — the FK is
       * `on delete set null`, so those memberships lose their label and keep their people. A
       * dialog that said only *"are you sure"* would leave somebody guessing which of the two.
       */}
      <Dialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
        title={t('settings.projectRoles.delete')}
        description={
          deleting === null
            ? ''
            : deleting.usageCount === 0
              ? t('settings.projectRoles.deleteUnused', { name: deleting.name })
              : t('settings.projectRoles.deleteUsed', {
                  name: deleting.name,
                  count: deleting.usageCount,
                })
        }
        closeLabel={t('common.close')}
      >
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setDeleting(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            size="lg"
            onClick={() => {
              if (deleting !== null) onDelete(deleting.id);
              setDeleting(null);
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function RoleDialog({
  editing,
  busy,
  serverError,
  onClose,
  onSubmit,
}: {
  readonly editing: ProjectRole | 'new' | null;
  /* 26 Oct 2026 · the save in flight, and the server's refusal of it. */
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (name: string, color: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    setName(editing === 'new' ? '' : editing.name);
    setColor(editing === 'new' ? DEFAULT_COLOR : editing.color);
  }, [editing]);

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(editing === 'new' ? 'settings.projectRoles.new' : 'settings.projectRoles.edit')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('settings.projectRoles.name')}>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('settings.projectRoles.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>

        <ColorPicker value={color} onChange={setColor} label={t('common.color')} />

        {(error ?? serverError) === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </div>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="lg"
            disabled={busy}
            onClick={() => {
              /* ⚠️ The duplicate check is the SERVER's — it is the only one that can see every
                 role — so this refuses only what it can know locally. */
              if (name.trim() === '') {
                setError(t('settings.projectRoles.error.name'));
                return;
              }
              onSubmit(name.trim(), color);
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
